import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppShell, { NAV_PERM, canSee, isFullRole } from "@/components/AppShell";
import BrandingLoader from "@/components/BrandingLoader";
import Login from "@/pages/Login";
import AppDownload from "@/pages/AppDownload";
import Dashboard from "@/pages/Dashboard";
import WhatsApp from "@/pages/WhatsApp";
import Templates from "@/pages/Templates";
import Clients from "@/pages/Clients";
import Messages from "@/pages/Messages";
import ChatLogs from "@/pages/ChatLogs";
import Settings from "@/pages/Settings";
import MenuBuilder from "@/pages/MenuBuilder";
import Payments from "@/pages/Payments";
import ManualCharges from "@/pages/ManualCharges";
import BulkBilling from "@/pages/BulkBilling";
import Marketing from "@/pages/Marketing";
import Logs from "@/pages/Logs";
import Health from "@/pages/Health";
import Team from "@/pages/Team";
import Admin from "@/pages/Admin";
import { Megaphone } from "lucide-react";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-500 text-sm">
        Carregando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const tenantStatus = user.tenant_status || "active";
  const isSuper = !!user.is_superadmin;
  if (!isSuper && tenantStatus !== "active") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 grain-overlay p-6">
        <div className="max-w-lg w-full border border-orange-500/30 bg-slate-900/70 rounded-lg p-8 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-400 mb-2">Conta {tenantStatus}</div>
          <h1 className="font-outfit text-2xl font-bold text-white">Acesso restrito</h1>
          <p className="mt-3 text-sm text-slate-400">
            {user.status_message || "Sua conta ainda não está ativa. Entre em contato com o administrador do Quitaí."}
          </p>
          <p className="mt-6 text-xs text-slate-500">
            E-mail: <span className="font-mono-jb text-emerald-300">{user.email}</span>
          </p>
        </div>
      </div>
    );
  }
  if (!isFullRole(user)) {
    if (!canSee(user, location.pathname)) {
      const first = Object.keys(NAV_PERM).find((p) => canSee(user, p));
      if (first && first !== location.pathname) return <Navigate to={first} replace />;
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-400 p-6 text-center">
          Seu perfil ainda não tem nenhuma tela liberada. Fale com o administrador da empresa.
        </div>
      );
    }
  }
  return <AppShell>{children}</AppShell>;
}

function RequireMarketing({ children }) {
  const { user } = useAuth();
  if (user && !user.marketing_module_enabled && !user.is_superadmin) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center border border-fuchsia-500/20 bg-fuchsia-500/5 rounded-lg p-10">
        <Megaphone className="w-8 h-8 mx-auto mb-3 text-fuchsia-400" />
        <h1 className="font-outfit text-2xl font-bold text-white">Módulo Marketing não contratado</h1>
        <p className="mt-3 text-sm text-slate-400">
          Envie mensagens em massa pros seus clientes, agendadas ou recorrentes, com avisos de manutenção e campanhas
          de marketing. Fale com o administrador do Quitaí para habilitar esse módulo na sua empresa.
        </p>
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingLoader />
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/app" element={<AppDownload />} />
          <Route path="/" element={<Protected><Dashboard /></Protected>} />
          <Route path="/whatsapp" element={<Protected><WhatsApp /></Protected>} />
          <Route path="/menu" element={<Protected><MenuBuilder /></Protected>} />
          <Route path="/templates" element={<Protected><Templates /></Protected>} />
          <Route path="/recebimentos" element={<Protected><Payments /></Protected>} />
          <Route path="/cobranca-avulsa" element={<Protected><ManualCharges /></Protected>} />
          <Route path="/clientes" element={<Protected><Clients /></Protected>} />
          <Route path="/cobranca-lote" element={<Protected><BulkBilling /></Protected>} />
          <Route path="/marketing" element={<Protected><RequireMarketing><Marketing /></RequireMarketing></Protected>} />
          <Route path="/envios" element={<Protected><Messages /></Protected>} />
          <Route path="/logs" element={<Protected><Logs /></Protected>} />
          <Route path="/saude" element={<Protected><Health /></Protected>} />
          <Route path="/atendimentos" element={<Protected><ChatLogs /></Protected>} />
          <Route path="/equipe" element={<Protected><Team /></Protected>} />
          <Route path="/config" element={<Protected><Settings /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
